import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Button } from './ui/button';
import { Repeat, ArrowRightLeft, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const VendorAlternativesCard = ({ itemId, currentVendorId, onVendorSwapped }) => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [swapping, setSwapping] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api().get(`/items/${itemId}/vendor-alternatives`);
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [itemId, currentVendorId]);

  const handleSwap = async (alt) => {
    if (!window.confirm(`Switch preferred vendor to "${alt.vendor_name}"?`)) return;
    setSwapping(alt.vendor_id);
    try {
      // Fetch full item, mutate vendor, PUT back
      const { data: it } = await api().get(`/items/${itemId}`);
      const payload = {
        ...it,
        preferred_vendor_id: alt.vendor_id,
      };
      // Strip server-managed fields the API doesn't accept
      delete payload.id;
      delete payload.created_at;
      await api().put(`/items/${itemId}`, payload);
      toast.success(`Preferred vendor swapped to ${alt.vendor_name}`);
      onVendorSwapped && onVendorSwapped(alt.vendor_id);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to swap vendor');
    } finally {
      setSwapping(null);
    }
  };

  return (
    <Card className="industrial-card" data-testid="vendor-alternatives-card">
      <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between flex">
        <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Repeat className="w-4 h-4" /> Vendor Substitution Recommender
        </h2>
        {data && (
          <span className="text-xs text-slate-500 mono">
            {data.totals.shown} of {data.totals.considered} candidate{data.totals.considered === 1 ? '' : 's'}
          </span>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-6 text-center text-sm text-slate-400">Scoring vendors…</div>
        ) : !data || data.alternatives.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500" data-testid="vendor-alternatives-empty">
            {data && data.current_vendor
              ? 'No alternative vendors found for this item category yet. Add more vendors and link them to similar items.'
              : 'Set a preferred vendor and category on this item to get substitution recommendations.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor</TableHead>
                  <TableHead className="text-right">Risk Idx</TableHead>
                  <TableHead className="text-right">Defect %</TableHead>
                  <TableHead className="text-right">Lead (d)</TableHead>
                  <TableHead className="text-right">Latest $</TableHead>
                  <TableHead>Reasons</TableHead>
                  <TableHead className="text-right">Score</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.alternatives.map((alt, idx) => (
                  <TableRow key={alt.vendor_id} data-testid={`vendor-alt-row-${alt.vendor_id}`}>
                    <TableCell className="font-medium text-slate-800">
                      {idx === 0 && <CheckCircle2 className="inline w-3 h-3 mr-1 text-emerald-600" />}
                      {alt.vendor_name}
                      {alt.has_supplied_sku && (
                        <div className="text-xs text-emerald-600">supplied this SKU before</div>
                      )}
                    </TableCell>
                    <TableCell className="text-right mono">
                      <span className={alt.risk_index >= 70 ? 'text-red-600' : alt.risk_index >= 40 ? 'text-orange-600' : 'text-emerald-700'}>
                        {alt.risk_index}
                      </span>
                    </TableCell>
                    <TableCell className="text-right mono">
                      <span className={alt.defect_rate_pct >= 5 ? 'text-red-600 font-semibold' : alt.defect_rate_pct > 0 ? 'text-amber-600' : 'text-slate-500'}>
                        {alt.defect_rate_pct}%
                      </span>
                    </TableCell>
                    <TableCell className="text-right mono text-slate-600">{alt.lead_time_days}</TableCell>
                    <TableCell className="text-right mono text-slate-600">
                      {alt.latest_unit_cost != null ? `$${alt.latest_unit_cost}` : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">
                      <ul className="list-disc list-inside space-y-0.5">
                        {alt.reasons.map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    </TableCell>
                    <TableCell className="text-right mono text-slate-700">{alt.score}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSwap(alt)}
                        disabled={swapping === alt.vendor_id}
                        className="rounded-sm"
                        data-testid={`vendor-alt-swap-${alt.vendor_id}`}
                      >
                        <ArrowRightLeft className="w-3 h-3 mr-1" />
                        {swapping === alt.vendor_id ? 'Swapping…' : 'Swap'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {data && data.current_vendor && (
          <div className="px-4 py-2 text-xs text-slate-500 border-t border-slate-100 bg-slate-50" data-testid="vendor-alt-current">
            Current: <span className="font-medium text-slate-700">{data.current_vendor.vendor_name}</span> ·
            risk {data.current_vendor.risk_index} · defect {data.current_vendor.defect_rate_pct}% ·
            lead {data.current_vendor.lead_time_days}d ·
            latest ${data.current_vendor.latest_unit_cost ?? '—'}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VendorAlternativesCard;
